import React, { useState } from 'react';
import { UploadCloud } from 'lucide-react';
import { uploadImage } from '../services/imageCrud';

const S3ImageUpload = ({ projectId, onImageUploaded, onError }) => {
  const [file, setFile] = useState(null);
  const [fileValid, setFileValid] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [stage, setStage] = useState("idle"); // idle | uploading | processing
  const [error, setError] = useState('');

  // Validate the file type and size
  const validateFile = (f) => {
    if (!f) return false;

    setError('');
    const okType = ['image/jpeg', 'image/jpg', 'image/webp'].includes(f.type);
    if (!okType) {
      const errorMsg = 'Only JPG / WEBP allowed';
      setError(errorMsg);
      if (onError) onError(errorMsg);
      return false;
    }
    if (f.size > 2 * 1024 * 1024) {
      const errorMsg = 'Max size 2 MB';
      setError(errorMsg);
      if (onError) onError(errorMsg);
      return false;
    }
    return true;
  };

  const handleFileAccepted = (f) => {
    setFile(f);
    setFileValid(validateFile(f));
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileAccepted(e.dataTransfer.files[0]);
    }
  };

  // Upload handler
  const handleUpload = async () => {
    if (!file || !fileValid) return;
    
    try {
      setStage("uploading");
      setError('');
      
      const res = await uploadImage(file, projectId);
      onImageUploaded({ 
        _id: res.imageId, 
        id: res.imageId,
        fileName: file.name 
      });
      setStage("idle");
    } catch (e) {
      if (e.message.includes("rejected")) {
        setError("Sorry, this image can't be used.");
      } else {
        setError("Upload failed – please try again.");
      }
      setStage("idle");
    }
  };

  return (
    <div className="max-w-md mx-auto">
      <div
        id="upload-area"
        className={`border-2 border-dashed rounded-lg p-8 flex flex-col items-center justify-center cursor-pointer transition-colors ${
          dragActive ? 'border-black' : 'border-gray-300'
        }`}
        onClick={() => document.getElementById('file-input').click()}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <UploadCloud size={48} className="text-gray-400" />
        <p className="mt-4 text-gray-600">
          Drag and drop an image here or{' '}
          <span className="text-black font-medium underline">choose a file</span>
        </p>

        <input
          type="file"
          id="file-input"
          accept=".jpg,.jpeg,.webp"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files[0];
            handleFileAccepted(f);
          }}
        />

        {file && (
          <p className="mt-2 text-sm text-green-600">Selected: {file.name}</p>
        )}

        <p className="mt-4 text-sm text-gray-500">Recommended aspect ratio: 4:3</p>
        <p className="mt-1 text-sm text-gray-500">Max 2 MB. Allowed file types: JPG, JPEG, WEBP.</p>
      </div>
      {error && <p className="text-red-600 text-sm mt-2">{error}</p>}

      {file && fileValid && (
        <div className="mt-4 flex justify-end">
          <button
            onClick={handleUpload}
            disabled={stage !== "idle"}
            className={`px-4 py-2 text-white rounded transition-colors ${
              stage !== "idle" ? 'bg-black/50 cursor-not-allowed' : 'bg-black hover:bg-gray-800'
            }`}
          >
            {stage === "uploading" ? "Uploading..." : stage === "processing" ? "Processing..." : "Upload Image"}
          </button>
        </div>
      )}
    </div>
  );
};

export default S3ImageUpload;